import type { Lang } from '../i18n/LangProvider'

type Texto = Record<Lang, string>

export interface SeccionManual {
  id: string
  icono: string
  titulo: Texto
  /** Roles que ven la sección; sin `roles`, la ve todo el mundo. */
  roles?: string[]
  puntos: Texto[]
}

// Petición del club (2026-10-31): "un manual de usuario accesible desde la propia app, porque hay
// bastante funcionalidad". Cada sección describe lo que hay en pantalla con los mismos nombres que los
// botones (en los dos idiomas) y solo se muestra a los roles que pueden usarlo. Si cambia una
// pantalla, hay que actualizar aquí su sección.
export const MANUAL: SeccionManual[] = [
  {
    id: 'inicio',
    icono: '🏁',
    titulo: { es: 'Primeros pasos', eu: 'Lehen urratsak' },
    puntos: [
      {
        es: 'Al entrar ves los equipos a los que tienes acceso. En el menú «Mis equipos» se eligen por categoría: UKE Eskola, F7 y F11.',
        eu: 'Sartzean, sarbidea duzun taldeak ikusten dituzu. «Nire taldeak» menuan kategoriaka aukeratzen dira: UKE Eskola, F7 eta F11.',
      },
      {
        es: 'En el móvil el menú se abre con ☰, arriba a la izquierda.',
        eu: 'Mugikorrean menua ☰ ikurrarekin irekitzen da, goian ezkerrean.',
      },
      {
        es: 'El idioma (euskera o castellano) se cambia con el selector de arriba y la app lo recuerda para la próxima vez.',
        eu: 'Hizkuntza (euskara edo gaztelania) goiko hautatzailearekin aldatzen da, eta aplikazioak hurrengorako gogoratzen du.',
      },
      {
        es: 'Pulsa la tarjeta de un equipo para entrar. Tiene las pestañas Plantilla, Asistencia y, en F7 y F11, Minutaje.',
        eu: 'Sakatu talde baten txartela sartzeko. Fitxa hauek ditu: Plantilla, Asistentzia eta, F7 eta F11 taldeetan, Minutajea.',
      },
      {
        es: '«← Volver» o el escudo de UKE Gara te devuelven al inicio.',
        eu: '«← Atzera» botoiak edo UKE Garako ezkutuak hasierara itzultzen zaituzte.',
      },
      {
        es: 'Qué ve cada rol: el entrenador, sus equipos; el coordinador, los equipos de su categoría y ALERTA; el director, todos los equipos, ALERTA y el Histórico; el administrador, además, el Backoffice.',
        eu: 'Rol bakoitzak zer ikusten duen: entrenatzaileak, bere taldeak; koordinatzaileak, bere kategoriako taldeak eta ALERTA; zuzendariak, talde guztiak, ALERTA eta Historikoa; administratzaileak, horrez gain, Backofficea.',
      },
    ],
  },
  {
    id: 'plantilla',
    icono: '👥',
    titulo: { es: 'Plantilla', eu: 'Plantilla' },
    puntos: [
      {
        es: 'Lista de las personas activas del equipo por grupos: Con ficha, Sin ficha, Entrenadores y Delegados, con su fecha de incorporación y su asistencia media.',
        eu: 'Taldeko pertsona aktiboen zerrenda, taldekatuta: Fitxadunak, Fitxarik gabekoak, Entrenatzaileak eta Ordezkariak, sartze-datarekin eta batez besteko asistentziarekin.',
      },
      {
        es: '«⬇ Excel del equipo» descarga los datos del equipo en Excel.',
        eu: '«⬇ Taldearen Excela» botoiak taldearen datuak Excelera jaisten ditu.',
      },
      {
        es: 'El administrador puede cambiar el orden arrastrando el asa ⠿ de cada persona (en ordenador).',
        eu: 'Administratzaileak ordena alda dezake pertsona bakoitzaren ⠿ heldulekua arrastatuta (ordenagailuan).',
      },
      {
        es: 'Para añadir personas, cambiarles el rol o darlas de baja se usa la pestaña Asistencia (botones «+» y ficha de cada persona) o el Backoffice.',
        eu: 'Pertsonak gehitzeko, rola aldatzeko edo baja emateko, erabili Asistentzia fitxa («+» botoiak eta pertsona bakoitzaren fitxa) edo Backofficea.',
      },
    ],
  },
  {
    id: 'asistencia',
    icono: '✅',
    titulo: { es: 'Asistencia en UKE Eskola y F7', eu: 'Asistentzia UKE Eskolan eta F7n' },
    puntos: [
      {
        es: 'Cada columna es un día del mes elegido (⚽ = partido). Los meses se cambian con los botones de arriba. La fila de fechas y la columna de nombres se quedan fijas al desplazarte.',
        eu: 'Zutabe bakoitza aukeratutako hilabeteko egun bat da (⚽ = partidua). Hilabeteak goiko botoiekin aldatzen dira. Daten errenkada eta izenen zutabea finko geratzen dira mugitzean.',
      },
      {
        es: 'Toca la casilla de un jugador para marcar ✓ (asiste); vuelve a tocarla para dejarla vacía. Vacía = no asiste: en los días ya pasados cuenta como falta.',
        eu: 'Sakatu jokalari baten laukia ✓ markatzeko (bertaratu da); sakatu berriro hutsik uzteko. Hutsik = ez da bertaratu: igarotako egunetan hutsegite gisa kontatzen da.',
      },
      {
        es: 'Para indicar el motivo de una falta o escribir una nota: clic derecho en la casilla (ordenador) o mantenla pulsada (móvil). Motivos: 🤒 Enfermo, 🩹 Lesión, 📄 Justificada y ❓ Sin motivo. Todos cuentan como falta en el porcentaje.',
        eu: 'Hutsegite baten arrazoia adierazteko edo ohar bat idazteko: eskuineko klika laukian (ordenagailua) edo eutsi sakatuta (mugikorra). Arrazoiak: 🤒 Gaixorik, 🩹 Lesioa, 📄 Justifikatua eta ❓ Arrazoirik gabe. Denak hutsegite gisa kontatzen dira ehunekoan.',
      },
      {
        es: 'Toca la cabecera de un día para elegir: «Todos asisten», «No hay entrenamiento (Festivo)» o «Se suspende el entrenamiento». Un día festivo o suspendido no cuenta en los porcentajes.',
        eu: 'Sakatu egun baten goiburua aukeratzeko: «Denak bertaratzen dira», «Entrenamendurik ez (Jaieguna)» edo «Entrenamendua bertan behera uzten da». Jaiegunak eta bertan behera utzitako egunak ez dira ehunekoetan kontatzen.',
      },
      {
        es: '«+ Añadir entrenamiento» crea un entrenamiento en la fecha que elijas; sirve también para recuperar un día marcado como festivo o suspendido.',
        eu: '«+ Entrenamendua» botoiak aukeratutako datan entrenamendu bat sortzen du; jaiegun edo bertan behera utzitako egun bat berreskuratzeko ere balio du.',
      },
      {
        es: '«Editar» permite quitar días que no correspondan; pulsa «Guardar» al terminar.',
        eu: '«Editatu» botoiarekin dagozkion ez diren egunak ken daitezke; amaitzean, sakatu «Gorde».',
      },
      {
        es: 'Los porcentajes Mensual y Anual se calculan solos. Los días futuros no cuentan.',
        eu: 'Hilabeteko eta Urteko ehunekoak automatikoki kalkulatzen dira. Etorkizuneko egunak ez dira kontatzen.',
      },
      {
        es: '«+ Jugador», «+ Entrenador» y «+ Delegado» añaden personas. Los entrenadores tienen su propia sección en la tabla; los delegados no cuentan en asistencia ni en minutaje.',
        eu: '«+ Jokalaria», «+ Entrenatzailea» eta «+ Ordezkaria» botoiek pertsonak gehitzen dituzte. Entrenatzaileek beren atala dute taulan; ordezkariak ez dira asistentzian ez minutajean kontatzen.',
      },
      {
        es: 'Pulsa el nombre de una persona para abrir su ficha: foto, nombre, rol, estadísticas, «⬇ Excel» individual y «🗑 Eliminar jugador».',
        eu: 'Sakatu pertsona baten izena bere fitxa irekitzeko: argazkia, izena, rola, estatistikak, banakako «⬇ Excel» eta «🗑 Jokalaria ezabatu».',
      },
      {
        es: 'Solo el administrador: con clic derecho o pulsación larga, «Eliminar esta sesión (solo para esta persona)» quita ese día a una persona (por ejemplo, si se incorporó más tarde).',
        eu: 'Administratzaileak bakarrik: eskuineko klikarekin edo luze sakatuta, «Saioa ezabatu (pertsona honentzat)» aukerak egun hori kentzen dio pertsona bati (adibidez, beranduago sartu bada).',
      },
      {
        es: '«⬇ CSV» descarga la asistencia.',
        eu: '«⬇ CSV» botoiak asistentzia jaisten du.',
      },
    ],
  },
  {
    id: 'asistencia-f11',
    icono: '🟩',
    titulo: { es: 'Asistencia en F11', eu: 'Asistentzia F11n' },
    puntos: [
      {
        es: 'Toca una casilla para elegir el estado: Asiste, Al margen (EM), Recupera (RC), Vacaciones (VA), Lesión (LS), Enfermo (EN), Trabajo (TR), Estudio (EX), Otros (OT) o No justificada (NJ).',
        eu: 'Sakatu lauki bat egoera aukeratzeko: Bertaratua, Bazterrean (EM), Berreskuratzen (RC), Oporrak (VA), Lesioa (LS), Gaixo (EN), Lana (TR), Ikasketa (EX), Beste (OT) edo Justifikatu gabe (NJ).',
      },
      {
        es: 'En F11 solo cuentan las sesiones marcadas: una casilla sin marcar (·) no suma ni resta.',
        eu: 'F11n markatutako saioak bakarrik kontatzen dira: markatu gabeko lauki batek (·) ez du ez gehitzen ez kentzen.',
      },
      {
        es: 'La cabecera de cada día tiene las mismas acciones que en UKE Eskola y F7: «Todos asisten», «No hay entrenamiento (Festivo)» y «Se suspende el entrenamiento».',
        eu: 'Egun bakoitzaren goiburuak UKE Eskolan eta F7n dituen ekintza berberak ditu: «Denak bertaratzen dira», «Entrenamendurik ez (Jaieguna)» eta «Entrenamendua bertan behera uzten da».',
      },
      {
        es: '«Leyenda» despliega el significado de cada código. Debajo están el «Resumen temporada» y la «Evolución mensual».',
        eu: '«Legenda» sakatuta kode bakoitzaren esanahia ikusten da. Azpian «Denboraldiko laburpena» eta «Hileroko bilakaera» daude.',
      },
      {
        es: '«+ Añadir entrenamiento», «Editar», «⬇ CSV», los botones «+» y la ficha de cada persona funcionan igual que en UKE Eskola y F7.',
        eu: '«+ Entrenamendua», «Editatu», «⬇ CSV», «+» botoiak eta pertsona bakoitzaren fitxa UKE Eskolan eta F7n bezala dabiltza.',
      },
    ],
  },
  {
    id: 'minutaje',
    icono: '⏱️',
    titulo: { es: 'Minutaje (F7 y F11)', eu: 'Minutajea (F7 eta F11)' },
    puntos: [
      {
        es: 'En «Jornada», pulsa «Nueva jornada» y rellena rival, fecha, campo (Local o Visitante) y goles a favor y en contra.',
        eu: '«Jardunaldia» atalean, sakatu «Jardunaldi berria» eta bete aurkaria, data, zelaia (Etxean edo Kanpoan) eta aldeko eta aurkako golak.',
      },
      {
        es: 'Para cada jugador indica si está convocado y si es titular, o por qué no viene (LES lesión, SAN sanción, ENF enfermo, VAC vacaciones, TR trabajo, NJ no justificado).',
        eu: 'Jokalari bakoitzarentzat adierazi deitua dagoen eta hasierako hamaikakoan dagoen, edo zergatik ez datorren (LES lesioa, SAN zigorra, ENF gaixorik, VAC oporrak, TR lana, NJ justifikatu gabe).',
      },
      {
        es: 'Apunta los minutos jugados (con atajos como «partido completo»), los goles y las tarjetas, y pulsa «💾 GUARDAR JORNADA».',
        eu: 'Idatzi jokatutako minutuak («partidu osoa» bezalako lasterbideekin), golak eta txartelak, eta sakatu «💾 JARDUNALDIA GORDE».',
      },
      {
        es: 'Cada 5 tarjetas amarillas, el jugador queda sancionado para la jornada siguiente.',
        eu: '5 txartel hori bakoitzeko, jokalaria zigortuta geratzen da hurrengo jardunaldirako.',
      },
      {
        es: 'En «Dashboard» están los KPIs del equipo y el control de participación: %TOTAL, %CONV y %DISP (sobre los partidos en los que el jugador estaba disponible). Con %DISP por debajo del 50 % sale «⚠️ Participación baja» y por debajo del 70 %, «👁 Participación media».',
        eu: '«Dashboarda» atalean taldearen KPIak eta parte-hartze kontrola daude: %TOTAL, %CONV eta %DISP (jokalaria eskuragarri zegoen partiduen gainean). %DISP % 50etik behera badago «⚠️ Parte-hartze txikia» agertzen da, eta % 70etik behera, «👁 Parte-hartze ertaina».',
      },
      {
        es: 'Pulsa un jugador para ver sus estadísticas. «☰ Tabla» y «⊞ Fichas» cambian la forma de verlo.',
        eu: 'Sakatu jokalari bat bere estatistikak ikusteko. «☰ Taula» eta «⊞ Fitxak» botoiek ikusteko modua aldatzen dute.',
      },
    ],
  },
  {
    id: 'alerta',
    icono: '🚦',
    titulo: { es: 'ALERTA', eu: 'ALERTA' },
    roles: ['admin', 'director', 'coordinador'],
    puntos: [
      {
        es: 'El botón ALERTA está en verde (✓) cuando todo está al día y en rojo si algún equipo tiene asistencia o minutaje pendiente. El coordinador solo ve los equipos de su categoría.',
        eu: 'ALERTA botoia berdea (✓) da dena egunean dagoenean, eta gorria talderen batek asistentzia edo minutajea egiteke badu. Koordinatzaileak bere kategoriako taldeak bakarrik ikusten ditu.',
      },
      {
        es: '«Estado equipos»: una tarjeta por equipo con semáforo. Rojo = días de entrenamiento ya pasados sin lista o jornadas jugadas sin minutaje. Un día deja de estar pendiente en cuanto se pasa lista. Los entrenamientos de hoy no cuentan hasta mañana.',
        eu: '«Taldeen egoera»: txartel bat talde bakoitzeko, semaforoarekin. Gorria = zerrendarik gabeko entrenamendu-egun igaroak edo minutajerik gabeko jardunaldi jokatuak. Zerrenda pasatu bezain laster, eguna ez dago egiteke. Gaurko entrenamenduak ez dira kontatzen bihar arte.',
      },
      {
        es: 'Pulsa la tarjeta para ver qué días o jornadas faltan. Pulsar el 🔴 envía un email de recordatorio a los entrenadores del equipo. Además, cada día a las 10:00 se envía un recordatorio automático (uno al día como mucho).',
        eu: 'Sakatu txartela falta diren egunak edo jardunaldiak ikusteko. 🔴 sakatuta, oroigarri bat bidaltzen zaie taldeko entrenatzaileei emailez. Gainera, egunero 10:00etan oroigarri automatiko bat bidaltzen da (egunean bat gehienez).',
      },
      {
        es: '«Entrenadores»: asistencia de los entrenadores de cada equipo.',
        eu: '«Entrenatzaileak»: talde bakoitzeko entrenatzaileen asistentzia.',
      },
      {
        es: '«JUGADORES»: elige un equipo para ver los jugadores con participación baja o media en el minutaje.',
        eu: '«JOKALARIAK»: aukeratu talde bat minutajean parte-hartze txikia edo ertaina duten jokalariak ikusteko.',
      },
      {
        es: '«Ausencias»: jugadores que llevan más de una semana sin venir, con el motivo, la última asistencia y las faltas seguidas.',
        eu: '«Absentziak»: astebete baino gehiago etorri gabe daramaten jokalariak, arrazoia, azken asistentzia eta jarraian egindako hutsegiteak.',
      },
      {
        es: '«Tarjetas» (F11): apercibidos (a una amarilla de la sanción) y sancionados, con la jornada en la que cumplen.',
        eu: '«Txartelak» (F11): ohartarazitakoak (zigorretik txartel hori batera) eta zigortuak, zigorra betetzen duten jardunaldiarekin.',
      },
    ],
  },
  {
    id: 'backoffice',
    icono: '🛠️',
    titulo: { es: 'Backoffice', eu: 'Backofficea' },
    roles: ['admin'],
    puntos: [
      {
        es: '«Usuarios»: crear y editar usuarios y sus roles (administrador, director, coordinador, entrenador). Al coordinador se le asigna una categoría y al entrenador sus equipos. «Restablecer contraseña» le envía un email.',
        eu: '«Erabiltzaileak»: erabiltzaileak eta haien rolak sortu eta editatu (administratzailea, zuzendaria, koordinatzailea, entrenatzailea). Koordinatzaileari kategoria bat esleitzen zaio, eta entrenatzaileari bere taldeak. «Pasahitza berrezarri» botoiak email bat bidaltzen dio.',
      },
      {
        es: '«Temporadas»: fechas de inicio y fin, bloques (Pretemporada, Temporada o Único) y festivos. Los festivos no generan entrenamiento. «Cerrar temporada» la deja en solo lectura; «Reabrir temporada» la vuelve a abrir.',
        eu: '«Denboraldiak»: hasiera- eta amaiera-datak, blokeak (Denboraldiaurrea, Denboraldia edo Bakarra) eta jaiegunak. Jaiegunek ez dute entrenamendurik sortzen. «Denboraldia itxi» botoiak irakurtzeko soilik uzten du; «Denboraldia berrireki» botoiak berriro irekitzen du.',
      },
      {
        es: '«Equipos»: datos del equipo (categoría, color e icono), días de entrenamiento (crean los entrenamientos automáticamente), minutos por periodo y número de periodos para el minutaje, y entrenadores asignados.',
        eu: '«Taldeak»: taldearen datuak (kategoria, kolorea eta ikonoa), entrenamendu egunak (entrenamenduak automatikoki sortzen dituzte), zatiko minutuak eta zati kopurua minutajerako, eta esleitutako entrenatzaileak.',
      },
      {
        es: 'En la plantilla de cada equipo: «Importar jugadores» desde Excel (con «Descargar plantilla» tienes el modelo), «+ Añadir jugador», fecha de baja y, en la «Zona de peligro», «Vaciar plantilla».',
        eu: 'Talde bakoitzaren plantillan: «Jokalariak inportatu» Exceletik («Txantiloia jaitsi» botoiarekin eredua duzu), «+ Jokalaria eskuz gehitu», baja data eta, «Arrisku-eremua» atalean, «Plantila hustu».',
      },
      {
        es: '«Uso y estado»: quién usa la app y cuándo (incluido quién no ha entrado nunca), actividad por equipo, tiempos de respuesta y los últimos fallos, del servidor o de la pantalla de alguien. Se puede ver para los últimos 7, 30 o 90 días.',
        eu: '«Erabilera eta egoera»: nork eta noiz erabiltzen duen aplikazioa (baita inoiz sartu ez dena ere), jarduera taldeka, erantzun-denborak eta azken akatsak, zerbitzarikoak edo norbaiten pantailakoak. Azken 7, 30 edo 90 egunetarako ikus daiteke.',
      },
      {
        es: 'En la pantalla de inicio, el administrador puede ordenar las categorías del menú y las tarjetas de los equipos arrastrándolas (en ordenador).',
        eu: 'Hasierako pantailan, administratzaileak menuko kategoriak eta taldeen txartelak ordena ditzake arrastatuta (ordenagailuan).',
      },
    ],
  },
  {
    id: 'historico',
    icono: '🗂',
    titulo: { es: 'Histórico y temporadas cerradas', eu: 'Historikoa eta itxitako denboraldiak' },
    puntos: [
      {
        es: 'Cuando una temporada está cerrada, sus equipos se pueden consultar pero no modificar; un aviso lo indica arriba.',
        eu: 'Denboraldi bat itxita dagoenean, bere taldeak kontsulta daitezke baina ezin dira aldatu; goian ohar batek adierazten du.',
      },
      {
        es: 'El administrador y el director tienen en «Histórico» las temporadas ya cerradas.',
        eu: 'Administratzaileak eta zuzendariak «Historikoa» atalean dituzte itxitako denboraldiak.',
      },
    ],
  },
]

export function seccionesPara(roles: string[] | undefined): SeccionManual[] {
  return MANUAL.filter((s) => !s.roles || s.roles.some((rol) => roles?.includes(rol)))
}
